import SearchIcon from '@mui/icons-material/Search';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { demeterPresentationTemplatesApi, demeterUsersApi } from '../../../Apis/Apis';
import AgGridBuilder from '../../../Components/AgGridBuilder/AgGridBuilder';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { ListPresentationTemplatesResponse, PresentationTemplateModel } from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import useApi from '../../Apis/Hooks/useApiHook';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import TextInput from '../../Components/Form/Inputs/TextInput';
import PageHeader from '../../Components/Headers/PageHeader';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import { ApiListResponse } from '../../Components/Tables/TableWrapper/ApiListResponse';
import PaginationTableWrapper from '../../Components/Tables/TableWrapper/PaginationTableWrapper';
import browserService from '../../Services/BrowserService';
import useLanguage from '../../Services/Language/useLanguageHook';
import PresentationTemplateActions from './PresentationTemplateActions';
import { columnDefinitions, defaultColumnDefinition, RendererParameters } from './PresentationTemplatesDefinitions';
import styles from './PresentationTemplatesPage.module.scss';

const PresentationTemplatesPage: React.FC = () => {
    const [translations] = useLanguage();
    const [searchParameters, setSearchParameters] = useSearchParameters();
    const [searchTerm, setSearchTerm] = useState<string | undefined>(searchParameters.searchTerm);
    const [skip, setSkip] = useState<number | undefined>(0);
    const [take, setTake] = useState<number | undefined>(applicationConstants.ItemsPerPage);
    const [apiResponse, setApiResponse] = useState<ListPresentationTemplatesResponse>();
    const navigate = useNavigate();

    const gridReference = useRef<AgGridReact>(null);

    const [loading, listPresentationTemplates, listPresentationTemplatesResponse] = useApi(
        () => demeterPresentationTemplatesApi.listPresentationTemplates(searchTerm, undefined, take, skip),
        { errorMessage: translations.presentationTemplates.errors.listPresentationTemplatesError },
    );

    const [, , listDemeterUsersResponse] = useApi(() => demeterUsersApi.listDemeterUserSummaries());

    useEffect(() => {
        browserService.scrollUp();
    }, []);

    useEffect(() => {
        if (!columnDefinitions || !listDemeterUsersResponse) {
            return;
        }
        const actionIndex = columnDefinitions.findIndex((element) => element.field === 'actions');
        columnDefinitions[actionIndex].cellRenderer = (parameters: RendererParameters) => (
            <PresentationTemplateActions
                presentationTemplate={parameters.data}
                users={listDemeterUsersResponse.rows ?? []}
                handleRefresh={listPresentationTemplates}
            />
        );
    }, [columnDefinitions, listDemeterUsersResponse]);

    useEffect(() => {
        if (!listPresentationTemplatesResponse?.rows) {
            return;
        }

        listPresentationTemplatesResponse.rows = listPresentationTemplatesResponse.rows.map((row: PresentationTemplateModel, index) => ({
            ...row,
            id: index,
        }));

        setApiResponse(listPresentationTemplatesResponse);
    }, [listPresentationTemplatesResponse]);

    const handleAdd = () => {
        const addPath = NavigationRoutes.PresentationTemplatesEdit.replace(':presentationTemplateGuid', applicationConstants.ZeroGuid);
        navigate(addPath);
    };

    const handleTextChange = (value: string) => {
        setSearchTerm(value);
        setSearchParameters({ ...searchParameters, searchTerm: value });
    };

    const handleSearch = () => {
        setSkip(0);
        listPresentationTemplates();
    };

    const isLoading = loading || !listDemeterUsersResponse;

    return (
        <div className={styles.master_page_container}>
            <PageHeader title={translations.page.presentationTemplates} testId="PresentationTemplatesPageHeader" />
            <div className={styles.presentation_templates_container}>
                <div className={styles.presentation_templates_description}>{translations.presentationTemplates.text.description}</div>
                <div className={styles.presentation_templates_actions_row}>
                    <div className={styles.presentation_templates_actions_search_container}>
                        <SearchIcon className={styles.presentation_templates_actions_search_icon} />
                        <TextInput
                            className={styles.presentation_templates_actions_search_input}
                            placeholder={translations.actions.search}
                            value={searchTerm}
                            handleTextChange={handleTextChange}
                            handleKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                            track={{
                                eventCategory: 'PresentationTemplates',
                                eventAction: 'SearchTermChanged',
                                eventValue: searchTerm,
                            }}
                        />
                    </div>
                    <LinkButton
                        title={translations.presentationTemplates.actions.add}
                        type={LinkButtonType.White}
                        onClick={() => handleAdd()}
                        track={{ eventCategory: 'PresentationTemplates', eventAction: 'AddClicked' }}
                    />
                </div>

                <div className={styles.presentation_templates_table}>
                    <PaginationTableWrapper
                        apiListResponse={apiResponse}
                        showPagination
                        handlePagination={(apiListResponse: ApiListResponse) => setSkip(apiListResponse.skip ?? 0)}
                        handleTakeCountUpdate={(apiListResponse: ApiListResponse) => setTake(apiListResponse.take ?? applicationConstants.ItemsPerPage)}
                        track={{ eventCategory: 'PresentationTemplates', eventAction: 'PaginationChanged' }}
                    >
                        <AgGridBuilder
                            gridRef={gridReference}
                            rowData={apiResponse?.rows ?? []}
                            columnDefinitions={columnDefinitions}
                            defaultColumnDefinition={defaultColumnDefinition}
                            gridHeightFull
                            domLayout="autoHeight"
                            testId="PresentationTemplatesTable"
                        />
                    </PaginationTableWrapper>
                </div>
            </div>
        </div>
    );
};
export default PresentationTemplatesPage;
